import { createFileRoute, useNavigate, useParams } from '@tanstack/react-router'
import { useTranslation } from 'react-i18next'
import { requirePermission } from '@/utils/auth'
import PageShell from '@/components/Common/PageShell'
import { useEffect, useMemo, useState } from 'react'
import { notifications } from '@mantine/notifications'
import { Button, Group, LoadingOverlay, SegmentedControl } from '@mantine/core'
import { DriversGenericTable } from '@/components/DriversTableGeneric'
import DriverModal, { DriverModalType } from '@/components/DriverModal'
import useDriverData from '@/hooks/useDriverData'
import { useDisclosure } from '@mantine/hooks'
import { QueryClient, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  getExternalDriversQueryKey,
  getInactiveDriversQueryKey,
  getAllDriversQueryKey,
  importExternalDriverMutation,
  associateExternalDriverMutation,
  updateDriverMutation,
  createDriverMutation,
} from '@/api/main/@tanstack/react-query.gen'
import { TbUserPlus } from 'react-icons/tb'
import { UnifiedDriver } from '@/types/driver'
import { PermissionGuard } from '@/utils/permissions'
import { useAuthStore } from '@/stores/auth'
import { CITY_NAME } from '@/meta'

export const Route = createFileRoute('/drivers/$filter')({
  beforeLoad: () => requirePermission('drivers'),
  component: DriversPage,
  parseParams: params => ({
    filter: params.filter || (CITY_NAME.toLowerCase() === 'prague' ? 'all' : 'internal'),
  }),
})

const invalidateQueries = (queryClient: QueryClient) => {
  queryClient.invalidateQueries({ queryKey: getExternalDriversQueryKey({}) })
  queryClient.invalidateQueries({ queryKey: getAllDriversQueryKey({}) })
  queryClient.invalidateQueries({ queryKey: getInactiveDriversQueryKey({}) })
}

function DriversPage() {
  const { t } = useTranslation('drivers')
  const { unifiedDrivers, isLoading } = useDriverData()
  const queryClient = useQueryClient()
  const navigate = useNavigate()

  const { filter: selectedFilter } = useParams({ from: '/drivers/$filter' })
  const isPrague = CITY_NAME.toLowerCase() === 'prague'
  const allowedFilters: string[] = isPrague ? ['all', 'internal', 'external', 'linked'] : ['internal']
  const normalizedSelectedFilter = allowedFilters.includes(selectedFilter) ? selectedFilter : 'internal'

  const [selectedDriver, setSelectedDriver] = useState<UnifiedDriver | null>(null)
  const [modalType, setModalType] = useState<DriverModalType | null>(null)
  const [opened, { open, close }] = useDisclosure(!!selectedDriver)

  const { userPermissions } = useAuthStore()

  useEffect(() => {
    if (selectedFilter !== normalizedSelectedFilter) {
      navigate({
        to: '/drivers/$filter',
        params: { filter: normalizedSelectedFilter },
        replace: true,
      })
    }
  }, [navigate, normalizedSelectedFilter, selectedFilter])

  const { mutate: importDriver } = useMutation({
    ...importExternalDriverMutation(),
    onSuccess: () => {
      invalidateQueries(queryClient)
      notifications.show({
        title: t('importDriver'),
        message: t('imported', { name: selectedDriver?.name }),
        color: 'green',
      })
      close()
    },
  })

  const { mutate: linkDriver } = useMutation({
    ...associateExternalDriverMutation(),
    onSuccess: () => {
      invalidateQueries(queryClient)
      notifications.show({
        title: t('linkDriver'),
        message: t('linkedTo', { name: selectedDriver?.name }),
        color: 'green',
      })
      close()
    },
  })

  const { mutate: editDriver } = useMutation({
    ...updateDriverMutation(),
    onSuccess: () => {
      invalidateQueries(queryClient)
      notifications.show({
        title: t('editDriver'),
        message: t('edited', { name: selectedDriver?.name }),
        color: 'green',
      })
      close()
    },
    onError: () => {
      notifications.show({
        title: t('editDriver'),
        message: t('failedToEdit', { name: selectedDriver?.name }),
        color: 'red',
      })
      close()
    },
  })

  const { mutate: createDriver } = useMutation({
    ...createDriverMutation(),
    onSuccess: data => {
      invalidateQueries(queryClient)
      notifications.show({
        title: t('createDriver'),
        message: t('created', { name: data?.name }),
        color: 'green',
      })
      close()
    },
  })

  const modalAction = (driver?: UnifiedDriver | null) => {
    if (modalType === DriverModalType.IMPORT) {
      if (selectedDriver?.externalId) {
        importDriver({
          path: {
            external_id: selectedDriver.externalId,
          },
        })
      }
    }

    if (modalType === DriverModalType.LINK) {
      if (selectedDriver?.externalId && driver?.internalId) {
        linkDriver({
          query: {
            external_id: selectedDriver.externalId,
            driver_id: driver?.internalId,
          },
        })
      }
    }

    if (modalType === DriverModalType.EDIT) {
      if (driver) {
        const { name, phone, email, active } = driver

        editDriver({
          path: {
            driver_id: Number(driver.id),
          },
          body: {
            name,
            phone,
            email,
            active,
          },
        })
      }
    }

    if (modalType === DriverModalType.CREATE) {
      if (driver?.name) {
        createDriver({
          body: {
            name: driver?.name,
            phone: driver?.phone,
            email: driver?.email,
            active: driver?.active,
          },
        })
      }
    }
  }

  const handleImportDriver = (driver: UnifiedDriver) => {
    setModalType(DriverModalType.IMPORT)
    setSelectedDriver(driver)
    open()
  }

  const handleLinkDriver = (driver: UnifiedDriver) => {
    setModalType(DriverModalType.LINK)
    setSelectedDriver(driver)
    open()
  }

  const handleEditDriver = (driver: UnifiedDriver) => {
    setModalType(DriverModalType.EDIT)
    setSelectedDriver(driver)
    open()
  }

  const handleCreateDriver = () => {
    setModalType(DriverModalType.CREATE)
    open()
  }

  const internalDrivers = useMemo(() => unifiedDrivers.filter(driver => driver.type === 'internal'), [unifiedDrivers])

  return (
    <>
      <LoadingOverlay visible={isLoading} />

      <PageShell
        title={t('drivers')}
        action={
          <Group justify="space-between" align="center" gap="xs">
            <SegmentedControl
              data={
                isPrague
                  ? [
                      { label: t('all'), value: 'all' },
                      { label: t('internal'), value: 'internal' },
                      { label: t('external'), value: 'external' },
                      { label: t('linked'), value: 'linked' },
                    ]
                  : [{ label: t('internal'), value: 'internal' }]
              }
              transitionDuration={300}
              transitionTimingFunction="linear"
              value={normalizedSelectedFilter}
              onChange={value => navigate({ to: '/drivers/$filter', params: { filter: value } })}
            />
            <PermissionGuard userPermissions={userPermissions} resource="drivers" action="write">
              <Button
                onClick={() => handleCreateDriver()}
                size="xs"
                color="blue"
                leftSection={<TbUserPlus size={16} />}
              >
                {t('createDriver')}
              </Button>
            </PermissionGuard>
          </Group>
        }
      >
        <DriversGenericTable
          drivers={unifiedDrivers}
          selectedFilter={normalizedSelectedFilter as 'internal' | 'external' | 'linked' | 'all'}
          onImportDriver={handleImportDriver}
          onLinkDriver={handleLinkDriver}
          onEditDriver={handleEditDriver}
        />
      </PageShell>
      <DriverModal
        driver={selectedDriver}
        type={modalType}
        opened={opened}
        onClose={close}
        onConfirm={modalAction}
        internalDrivers={internalDrivers}
        showCreateWarning={isPrague}
      />
    </>
  )
}
